import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip, { ADMIN_STATUS_CHIP } from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import { ADMIN_ACCESS_MODES } from '../../utils/sessionUtils'

const DEFAULT_SEARCH_SELECT = {
    searchRole: '',
    searchStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchRole: {
        param: 'role',
        defaultValue: DEFAULT_SEARCH_SELECT.searchRole,
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
    },
}

const ACCESS_MODE_LABELS = {
    [ADMIN_ACCESS_MODES.DEFAULT]: '전체',
    [ADMIN_ACCESS_MODES.COUNTRY_ANALYTICS_ONLY]: '국가별 지표 전용',
}

const getAccessModeLabel = (accessMode) => ACCESS_MODE_LABELS[accessMode] || ACCESS_MODE_LABELS[ADMIN_ACCESS_MODES.DEFAULT]

export default function Admins({ role }) {
    const history = useHistory()
    const location = useLocation()
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    }))
    const initialListState = initialListStateRef.current
    const silentListRefreshRef = useRef(false)
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/admin/getAdminList', {
                page: curPage,
                offset: offset,
                searchText: searchQuery,
                searchSelect: searchSelect,
                role: role
            })

            if (data.code === 1) {
                setTotalCount(data.data.count || 0)
                setData(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '관리자 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '관리자 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (loading) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
        setSearchQuery((searchText || '').trim())
        setCurPage(1)
    }

    const resetFilters = () => {
        if (loading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (loading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const hasActiveFilters = Boolean(
        role === 5 &&
        (
            searchQuery ||
            searchSelect.searchRole ||
            searchSelect.searchStatus
        )
    )

    // 목록 상태에 맞는 빈 화면을 한 곳에서 결정한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="관리자 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button variant="outlined" size="small" onClick={() => getData(false)} disabled={loading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 어드민이 없습니다."
                    description="이메일, 이름, 업체명, 연락처 검색어와 등급/상태 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={loading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 어드민이 없습니다."
                description="어드민을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>어드민 리스트</Title>
                    {role === 5 &&
                        <ListFilterBar>
                            <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={loading} />}>
                                <InputBase
                                    sx={LIST_FILTER_SEARCH_FIELD_SX}
                                    placeholder="이메일/이름/업체명/연락처"
                                    type="search"
                                    name="searchText"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    disabled={loading}
                                />
                                <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                                <Divider sx={{ height: 28, m: 0.5, marginRight: 2 }} orientation="vertical" />
                                <TextField
                                    label="등급"
                                    select
                                    name="searchRole"
                                    value={searchSelect.searchRole}
                                    onChange={updateSearchSelect}
                                    size="small"
                                    disabled={loading}
                                    sx={{ minWidth: 150 }}
                                >
                                    <MenuItem value=''>전체</MenuItem>
                                    <MenuItem value='0'>매니저</MenuItem>
                                    <MenuItem value='1'>어드민</MenuItem>
                                    <MenuItem value='2'>슈퍼어드민</MenuItem>
                                </TextField>
                                <TextField
                                    label="상태"
                                    select
                                    name="searchStatus"
                                    value={searchSelect.searchStatus}
                                    onChange={updateSearchSelect}
                                    size="small"
                                    disabled={loading}
                                    sx={{ minWidth: 150 }}
                                >
                                    <MenuItem value=''>전체</MenuItem>
                                    <MenuItem value='0'>거부</MenuItem>
                                    <MenuItem value='1'>승인</MenuItem>
                                </TextField>
                            </ListFilterForm>
                        </ListFilterBar>
                    }
                    {role === 5 &&
                        <ListTableToolbar
                            right={(
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<Add />}
                                    size="small"
                                    disabled={loading}
                                    sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                    onClick={() => history.push(createListLinkTarget({
                                        pathname: '/new-admin',
                                        search: location.search,
                                        curPage: curPage,
                                        searchText: searchQuery,
                                        searchSelect: searchSelect
                                    }))}
                                >
                                    등록
                                </Button>
                            )}
                        />
                    }
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: 920 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이메일</TableCell>
                                <TableCell>이름/업체명</TableCell>
                                <TableCell>연락처</TableCell>
                                <TableCell>등급</TableCell>
                                <TableCell>접근 범위</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>등록일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? (
                                data.map((row) => (
                                    <TableRow key={row.adminId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.phone}</TableCell>
                                        <TableCell>{row.role}</TableCell>
                                        <TableCell>{getAccessModeLabel(row.accessMode)}</TableCell>
                                        <TableCell><StatusChip status={row.status} config={ADMIN_STATUS_CHIP} /></TableCell>
                                        <TableCell>{row.regTime}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup>
                                                <EditIcon path={'admin'} id={row.adminId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} disabled={loading} />
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
